<template>
  <div class="incidents-in-europe graphic-container" :class="{ 'graphic-container--social-mode': this.socialMode }">
    <div v-if="showHeading" class="incidents-in-europe__heading">
      <h3 class="chart-hed">
        Reports of suspected device related incidents increasing across Europe
      </h3>
    </div>
    <div class="clearfix mb-2">
      <button class="btn btn-primary btn-squared btn-sm float-right" :class="{ active: showOverlay }" @click="toggleOverlay()" title="About this chart" v-b-tooltip.hover.left>
        <i class="fas fa-info"></i>
        <span class="sr-only">About this chart</span>
      </button>
      <form class="form-inline" v-if="!noSelector">
        <div class="form-group m-0">
          <label for="country-input" class="mr-2">
            Incidents in
          </label>
          <select v-model="selectedCountry" class="custom-select custom-select-sm w-auto" id="country-input">
            <option v-for="country in countries" :key="country">
              {{ country }}
            </option>
          </select>
        </div>
      </form>
    </div>
    <div class="incidents-in-europe__chart">
      <transition name="scale">
        <div v-if="showOverlay" class="incidents-in-europe__chart__overlay">
          <p>
            ICIJ asked more than 30 countries about how many reports they’d received over the past decade. Eighteen regulators, responsible for the safety of 85 percent of EU citizens, responded.
          </p>
          <p class="text-muted small">
            National regulatory agencies collect reports in different ways, and, in some countries, the total number may include duplicates. The reports are collected to help governments monitor the safety of devices. They are not shared with doctors and the public. Manufacturers are lobbying to keep them confidential.
          </p>
          <button class="btn btn-secondary btn-sm font-weight-bold text-uppercase" @click="toggleOverlay(false)">
            See the chart
          </button>
        </div>
      </transition>
      <column-chart :chart-height-ratio="baseHeightRatio" :key="selectedCountry" :chart-data="chartData" :chart-options="chartOptions"></column-chart>
      <div class="text-muted small">
        <div v-if="showNoDataWarning">
          *No data for this year
        </div>
        <div v-if="selectedCountry == 'France'">
          **Official figures exclude a spike in incidents related to the PIP breast implant scandal for 2012 and 2013
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import compact from 'lodash/compact'
  import every from 'lodash/every'
  import keys from 'lodash/keys'
  import map from 'lodash/map'
  import reduce from 'lodash/reduce'
  import some from 'lodash/some'

  import bTooltip from 'bootstrap-vue/es/directives/tooltip/tooltip'
  import * as d3 from 'd3';

  import deviceRelatedHarmInEurope from '../assets/device-related-incidents-in-europe.json'
  import ColumnChart from '@/components/ColumnChart.vue'
  import embeddable from '../mixins/embeddable'

  export default {
    name: 'DeviceRelatedHarmInEurope',
    mixins: [embeddable],
    directives: {
      bTooltip
    },
    components: {
      ColumnChart
    },
    props: {
      defaultCountry: {
        type: String,
        default: 'Germany',
        validator: (val) => keys(deviceRelatedHarmInEurope).indexOf(val) !== -1
      },
      noSelector: {
        type: Boolean
      },
      noOverlay: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        selectedCountry:  this.defaultCountry,
        showOverlay: !this.noOverlay,
        chartOptions: {
          chartName: "deviceRelatedHarmInEurope",
          margin: {
            top: 20,
            bottom: 40
          },
          seriesName: "value",
          barColor: "#3B98AB",
          labelWidth: 50,
          xAxisTickFormat: this.xAxisTickFormat,
          yAxisTickFormat: d => d === 0 ? 0 : d3.format(",.2r")(d),
          yAxisTicks: 5,
        }
      }
    },
    methods: {
      countryData (name) {
        return reduce(deviceRelatedHarmInEurope[name], (res, value, date) => {
          res.push({ date, [this.chartOptions.seriesName]: value })
          return res
        }, [])
      },
      toggleOverlay (toggle = !this.showOverlay) {
        this.showOverlay = toggle
        return toggle
      },
      xAxisTickFormat (year) {
        if (this.narrowWidth) {
          return this.xAxisYearFormat(year)
        }
        return this.hasDataForYear(year) ? year : year + "*"
      },
      hasDataForYear(year) {
        return some(this.countryData(this.selectedCountry), (d) => {
          return d.date === year && d.value !== ""
        })
      }
    },
    watch: {
      selectedCountry () {
        this.toggleOverlay(false)
      }
    },
    computed: {
      chartData () {
        return this.countryData(this.selectedCountry)
      },
      hasThousandSuffix () {
        const values = compact(map(this.chartData, 'value'))
        return every(values, value => value > 1000)
      },
      hasMissingData () {
        return some(this.countryData(this.selectedCountry), (d) => d.value === "")
      },
      countries () {
        return keys(deviceRelatedHarmInEurope).sort()
      },
      showNoDataWarning() {
        return this.hasMissingData && !this.narrowWidth
      }
    }
  }
</script>

<style lang="scss">
  @import '~node_modules/bootstrap/scss/_mixins.scss';

  .incidents-in-europe {

    .scale-enter-active, .scale-leave-active {
      transition: .3s;
    }

    .scale-enter, .scale-leave-to {
      opacity: 0;
      transform: scale(0);
    }

    &__chart {
      position: relative;

      &__overlay {
        transform-origin: top right;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        min-height: 100%;
        padding: $spacer;
        background: rgba($primary, 0.9);
        color: text-contrast($primary);

        @include media-breakpoint-down(sm) {
          font-size: .9rem;
        }

        .text-muted {
          color: #fff !important;
          opacity: .7;
        }

        &:after {
          content: "";
          position: absolute;
          right: 0;
          bottom: 100%;
          border: transparent solid $line-height-base * 1.4rem / 4;
          border-bottom-color: rgba($primary, 0.9);
          transform: translateX(-50%);
        }
      }
    }
  }
</style>
